/**
 * Shared SMS Contacts Tab component with:
 *  - Contact groups (create, rename, delete, color)
 *  - Bulk import (paste Name,Phone per line)
 *  - Group filter
 *  - Individual add / delete
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Plus, Trash2, Search, Loader2, ChevronLeft, ChevronRight,
  Users, Upload, X, Check, Edit2, GraduationCap, FolderPlus
} from 'lucide-react';
import { toast } from 'sonner';
import defaultApi from '../../api';

const PRESET_COLORS = [
  '#3B82F6','#10B981','#8B5CF6','#F59E0B','#EF4444',
  '#14B8A6','#F97316','#06B6D4','#EC4899','#6366F1',
];

// ── Color dot ──────────────────────────────────────────────────────────────
function ColorDot({ color, size = 10 }) {
  return <span style={{ background: color, width: size, height: size, borderRadius: '50%', display: 'inline-block', flexShrink: 0 }} />;
}

// ── Group Chip ─────────────────────────────────────────────────────────────
function GroupChip({ group, selected, onClick, onDelete }) {
  return (
    <span onClick={onClick}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold cursor-pointer transition-all border select-none
        ${selected ? 'text-white shadow-sm' : 'bg-white text-slate-700 border-slate-200 hover:border-slate-300'}`}
      style={selected ? { background: group.color, borderColor: group.color } : {}}>
      <ColorDot color={group.color} />
      {group.name}
      <span className="ml-0.5 text-[10px] opacity-70">({group.contact_count ?? 0})</span>
      {onDelete && (
        <button onClick={e => { e.stopPropagation(); onDelete(group.id); }}
          className="ml-0.5 opacity-60 hover:opacity-100">
          <X className="w-2.5 h-2.5" />
        </button>
      )}
    </span>
  );
}

// ── Bulk Import Modal ──────────────────────────────────────────────────────
function BulkImportModal({ groups, bulkApiPath, onDone, onClose, api }) {
  const [raw, setRaw]         = useState('');
  const [groupId, setGroupId] = useState('');
  const [preview, setPreview] = useState([]);
  const [saving, setSaving]   = useState(false);

  const parsePreview = useCallback((text) => {
    // Split by comma OR newline, take only digit tokens
    const tokens = text.split(/[\n,]+/).map(t => t.trim()).filter(t => t && /^\d[\d\s\-+]{6,}$/.test(t));
    return tokens.slice(0, 10).map(t => ({ name: '', phone: t.replace(/[\s\-]/g,'') }));
  }, []);

  useEffect(() => { setPreview(parsePreview(raw)); }, [raw, parsePreview]);

  // Count tokens (comma or newline separated numbers)
  const lineCount = raw.split(/[\n,]+/).map(t => t.trim()).filter(t => t && /^\d[\d\s\-+]{6,}$/.test(t)).length;

  const handleImport = async () => {
    if (!raw.trim()) return toast.error('Paste some contacts first');
    setSaving(true);
    try {
      const r = await api.post(bulkApiPath, { raw_text: raw, group_id: groupId || undefined });
      toast.success(r.message || 'Imported!');
      onDone();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Import failed');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-3xl w-full max-w-xl shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-6 pb-4 border-b border-slate-100">
          <div>
            <h3 className="font-extrabold text-slate-900 text-lg">Bulk Import Contacts</h3>
            <p className="text-xs text-slate-500 mt-0.5">Paste one contact per line</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-xl hover:bg-slate-100 text-slate-400"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-6 space-y-4">
          {/* Format hint */}
          <div className="bg-blue-50 rounded-xl p-3 text-xs text-blue-700 space-y-1">
            <p className="font-bold">Just paste the numbers — separated by commas or new lines:</p>
            <p className="font-mono">Comma-separated → 08012345678, 08023456789, 09012345678</p>
            <p className="font-mono">One per line → paste from Excel, WhatsApp, or any source</p>
          </div>

          {/* Textarea */}
          <div>
            <div className="flex justify-between items-center mb-1.5">
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">Contacts</label>
              <span className="text-xs text-slate-400">{lineCount} entries</span>
            </div>
            <textarea value={raw} onChange={e => setRaw(e.target.value)}
              rows={8} placeholder={"08012345678, 08023456789, 09012345678\n08034567890\n07012345678, 08056789012"}
              className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm font-mono outline-none focus:border-blue-400 resize-none" />
          </div>

          {/* Group assignment */}
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Assign to Group <span className="font-normal text-slate-400">(optional)</span></label>
            <select value={groupId} onChange={e => setGroupId(e.target.value)}
              className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-blue-400">
              <option value="">No group</option>
              {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
            </select>
          </div>

          {/* Preview */}
          {preview.length > 0 && (
            <div>
              <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">Preview (first {preview.length})</p>
              <div className="space-y-1 max-h-32 overflow-y-auto">
                {preview.map((p, i) => (
                  <div key={i} className="flex items-center gap-3 text-xs bg-slate-50 rounded-lg px-3 py-1.5">
                    <span className="font-mono text-slate-700">{p.phone}</span>
                  </div>
                ))}
                {lineCount > 10 && <p className="text-xs text-slate-400 text-center">…and {lineCount - 10} more</p>}
              </div>
            </div>
          )}

          <div className="flex gap-3 pt-1">
            <button onClick={onClose} className="flex-1 py-2.5 border border-slate-200 text-slate-600 rounded-xl text-sm font-semibold">Cancel</button>
            <button onClick={handleImport} disabled={saving || !raw.trim()}
              className="flex-1 py-2.5 bg-[#2563eb] text-white rounded-xl text-sm font-bold hover:bg-blue-700 disabled:opacity-40 transition-all flex items-center justify-center gap-2"
              data-testid="bulk-import-submit-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              Import {lineCount > 0 ? lineCount : ''} Contacts
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Group Manager Modal ────────────────────────────────────────────────────
function GroupManagerModal({ groups, groupsApiPath, onDone, onClose, api }) {
  const [newName, setNewName]   = useState('');
  const [newColor, setNewColor] = useState(PRESET_COLORS[0]);
  const [creating, setCreating] = useState(false);
  const [editId, setEditId]     = useState(null);
  const [editName, setEditName] = useState('');

  const createGroup = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    try {
      await api.post(groupsApiPath, { name: newName.trim(), color: newColor });
      toast.success('Group created');
      setNewName('');
      onDone();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
    finally { setCreating(false); }
  };

  const renameGroup = async (id) => {
    if (!editName.trim()) return;
    try {
      await api.put(`${groupsApiPath}/${id}`, { name: editName.trim() });
      toast.success('Renamed');
      setEditId(null);
      onDone();
    } catch { toast.error('Failed to rename'); }
  };

  const deleteGroup = async (id) => {
    try {
      await api.delete(`${groupsApiPath}/${id}`);
      toast.success('Group deleted');
      onDone();
    } catch { toast.error('Failed to delete'); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-3xl w-full max-w-md shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-6 pb-4 border-b border-slate-100">
          <h3 className="font-extrabold text-slate-900 text-lg">Manage Groups</h3>
          <button onClick={onClose} className="p-2 rounded-xl hover:bg-slate-100 text-slate-400"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-5">
          {/* Create new */}
          <div className="space-y-2">
            <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">New Group</label>
            <div className="flex gap-2 items-center">
              <div className="flex gap-1 flex-wrap">
                {PRESET_COLORS.map(c => (
                  <button key={c} onClick={() => setNewColor(c)}
                    className={`w-5 h-5 rounded-full transition-transform ${newColor === c ? 'scale-125 ring-2 ring-offset-1' : ''}`}
                    style={{ background: c, ringColor: c }} />
                ))}
              </div>
            </div>
            <div className="flex gap-2">
              <input value={newName} onChange={e => setNewName(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && createGroup()}
                placeholder="Group name (e.g. Parents SSS3)"
                className="flex-1 border border-slate-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-blue-400" />
              <button onClick={createGroup} disabled={creating || !newName.trim()}
                className="px-4 py-2.5 bg-[#2563eb] text-white rounded-xl text-sm font-bold hover:bg-blue-700 disabled:opacity-40"
                data-testid="create-group-btn">
                {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {/* Existing groups */}
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {groups.length === 0 && <p className="text-sm text-slate-400 text-center py-4">No groups yet</p>}
            {groups.map(g => (
              <div key={g.id} className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl">
                <ColorDot color={g.color} size={12} />
                {editId === g.id ? (
                  <>
                    <input autoFocus value={editName} onChange={e => setEditName(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && renameGroup(g.id)}
                      className="flex-1 border border-blue-300 rounded-lg px-2 py-1 text-sm outline-none" />
                    <button onClick={() => renameGroup(g.id)} className="p-1 text-emerald-600 hover:bg-emerald-50 rounded"><Check className="w-4 h-4" /></button>
                    <button onClick={() => setEditId(null)} className="p-1 text-slate-400 hover:bg-slate-100 rounded"><X className="w-4 h-4" /></button>
                  </>
                ) : (
                  <>
                    <span className="flex-1 text-sm font-semibold text-slate-700">{g.name}</span>
                    <span className="text-xs text-slate-400">{g.contact_count ?? 0}</span>
                    <button onClick={() => { setEditId(g.id); setEditName(g.name); }} className="p-1 text-slate-400 hover:text-blue-500 hover:bg-blue-50 rounded"><Edit2 className="w-3.5 h-3.5" /></button>
                    <button onClick={() => deleteGroup(g.id)} className="p-1 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded"><Trash2 className="w-3.5 h-3.5" /></button>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════
// MAIN CONTACTS TAB COMPONENT
// ═══════════════════════════════════════════════════════

/**
 * @param {string} contactsApiBase  e.g. '/sms' → calls /sms/contacts, /sms/groups, /sms/contacts/bulk-import
 * @param {string} [importStudentsPath]  School only: '/school-sms/contacts/import-students'
 * @param {object} [apiClient]  Optional API client (defaults to regular api; pass partnerApi for partners)
 */
export default function SMSContactsTab({ contactsApiBase, importStudentsPath, apiClient }) {
  const api = apiClient || defaultApi;
  const [contacts, setContacts]   = useState([]);
  const [groups, setGroups]       = useState([]);
  const [search, setSearch]       = useState('');
  const [groupFilter, setGroupFilter] = useState('');
  const [total, setTotal]         = useState(0);
  const [page, setPage]           = useState(1);
  const [loading, setLoading]     = useState(false);
  const [showAdd, setShowAdd]     = useState(false);
  const [showBulk, setShowBulk]   = useState(false);
  const [showGroups, setShowGroups] = useState(false);
  const [form, setForm]           = useState({ name: '', phone: '', email: '', group_id: '' });
  const [importing, setImporting] = useState(false);

  const CONTACTS_PATH = `${contactsApiBase}/contacts`;
  const GROUPS_PATH   = `${contactsApiBase}/groups`;
  const BULK_PATH     = `${contactsApiBase}/contacts/bulk-import`;

  const loadGroups = useCallback(async () => {
    try {
      const r = await api.get(GROUPS_PATH);
      setGroups(r.data || []);
    } catch {}
  }, [api, GROUPS_PATH]);

  const loadContacts = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 20 });
      if (search) params.append('search', search);
      if (groupFilter) params.append('group_id', groupFilter);
      const r = await api.get(`${CONTACTS_PATH}?${params.toString()}`);
      setContacts(r.data || []);
      setTotal(r.total || 0);
    } finally { setLoading(false); }
  }, [api, CONTACTS_PATH, search, groupFilter, page]);

  useEffect(() => { loadGroups(); }, [loadGroups]);
  useEffect(() => { loadContacts(); }, [loadContacts]);

  const addContact = async () => {
    if (!form.phone.trim()) return toast.error('Phone is required');
    try {
      await api.post(CONTACTS_PATH, form);
      toast.success('Contact added');
      setShowAdd(false);
      setForm({ name: '', phone: '', email: '', group_id: '' });
      loadContacts();
      loadGroups();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
  };

  const deleteContact = async (id) => {
    try {
      await api.delete(`${CONTACTS_PATH}/${id}`);
      toast.success('Deleted');
      loadContacts();
      loadGroups();
    } catch { toast.error('Failed to delete'); }
  };

  const importParents = async () => {
    if (!importStudentsPath) return;
    setImporting(true);
    try {
      const r = await api.post(importStudentsPath, { type: 'parents' });
      toast.success(r.message || 'Imported');
      loadContacts();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Import failed'); }
    finally { setImporting(false); }
  };

  const totalPages = Math.max(1, Math.ceil(total / 20));
  const selectedGroup = groups.find(g => g.id === groupFilter);

  return (
    <div className="space-y-4">
      {/* Modals */}
      {showBulk   && <BulkImportModal   groups={groups} bulkApiPath={BULK_PATH}  onDone={() => { loadContacts(); loadGroups(); }} onClose={() => setShowBulk(false)} api={api} />}
      {showGroups && <GroupManagerModal groups={groups} groupsApiPath={GROUPS_PATH} onDone={() => loadGroups()} onClose={() => setShowGroups(false)} api={api} />}

      {/* Groups filter row */}
      <div className="flex items-center gap-2 flex-wrap">
        <span
          onClick={() => { setGroupFilter(''); setPage(1); }}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold cursor-pointer transition-all border select-none
            ${!groupFilter ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-700 border-slate-200 hover:border-slate-300'}`}>
          <Users className="w-3 h-3" /> All ({total})
        </span>
        {groups.map(g => (
          <GroupChip key={g.id} group={g}
            selected={groupFilter === g.id}
            onClick={() => { setGroupFilter(g.id === groupFilter ? '' : g.id); setPage(1); }}
          />
        ))}
        <button onClick={() => setShowGroups(true)}
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-bold text-slate-500 border border-dashed border-slate-300 hover:border-slate-400 hover:text-slate-700 transition-all"
          data-testid="manage-groups-btn">
          <FolderPlus className="w-3 h-3" /> Manage Groups
        </button>
      </div>

      {/* Search + actions */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-48">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
            placeholder={`Search${selectedGroup ? ` in ${selectedGroup.name}` : ''}…`}
            className="w-full pl-9 pr-4 py-2.5 border border-slate-200 rounded-xl text-sm outline-none focus:border-blue-400" />
        </div>
        {importStudentsPath && (
          <button onClick={importParents} disabled={importing}
            className="flex items-center gap-2 px-4 py-2.5 border border-slate-200 text-slate-700 rounded-xl font-semibold text-sm hover:bg-slate-50 transition-all">
            {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <GraduationCap className="w-4 h-4" />}
            Import Parents
          </button>
        )}
        <button onClick={() => setShowBulk(true)}
          className="flex items-center gap-2 px-4 py-2.5 border border-slate-200 text-slate-700 rounded-xl font-semibold text-sm hover:bg-slate-50 transition-all"
          data-testid="bulk-import-btn">
          <Upload className="w-4 h-4" /> Bulk Import
        </button>
        <button onClick={() => setShowAdd(true)}
          className="flex items-center gap-2 px-4 py-2.5 bg-[#2563eb] text-white rounded-xl font-bold text-sm hover:bg-blue-700 transition-all"
          data-testid="add-contact-btn">
          <Plus className="w-4 h-4" /> Add Contact
        </button>
      </div>

      {/* Add single contact */}
      {showAdd && (
        <div className="bg-blue-50 border border-blue-100 rounded-2xl p-5 space-y-3">
          <h4 className="font-bold text-slate-800">New Contact</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <input value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))}
              placeholder="Full name" className="border border-slate-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-blue-400" />
            <input value={form.phone} onChange={e => setForm(f => ({...f, phone: e.target.value}))}
              placeholder="Phone" className="border border-slate-200 rounded-xl px-3 py-2 text-sm font-mono outline-none focus:border-blue-400" />
            <input value={form.email} onChange={e => setForm(f => ({...f, email: e.target.value}))}
              placeholder="Email (optional)" className="border border-slate-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-blue-400" />
            <select value={form.group_id} onChange={e => setForm(f => ({...f, group_id: e.target.value}))}
              className="border border-slate-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-blue-400">
              <option value="">No group</option>
              {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
            </select>
          </div>
          <div className="flex gap-2">
            <button onClick={addContact} className="px-4 py-2 bg-[#2563eb] text-white rounded-xl text-sm font-bold">Save</button>
            <button onClick={() => setShowAdd(false)} className="px-4 py-2 border border-slate-200 text-slate-600 rounded-xl text-sm">Cancel</button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden">
        <div className="px-5 py-3.5 border-b border-slate-50 flex items-center gap-2">
          <p className="font-bold text-slate-700 text-sm">{total} contacts{selectedGroup ? ` in ${selectedGroup.name}` : ''}</p>
          {selectedGroup && <ColorDot color={selectedGroup.color} size={8} />}
        </div>
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-slate-300" /></div>
        ) : contacts.length === 0 ? (
          <div className="text-center py-12 text-slate-400 text-sm">
            {groupFilter ? 'No contacts in this group' : 'No contacts yet — add manually, bulk import, or use "Import Parents"'}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr>{['Name','Phone','Group',''].map(h => <th key={h} className="text-left px-5 py-3 text-xs font-bold text-slate-500 uppercase tracking-wide">{h}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {contacts.map(c => {
                const grp = groups.find(g => g.id === c.group_id);
                return (
                  <tr key={c.id} className="hover:bg-slate-50/50">
                    <td className="px-5 py-3 font-medium text-slate-800">{c.name || '—'}</td>
                    <td className="px-5 py-3 font-mono text-slate-600 text-xs">{c.phone}</td>
                    <td className="px-5 py-3">
                      {grp
                        ? <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-bold text-white" style={{ background: grp.color }}><ColorDot color="rgba(255,255,255,0.6)" size={6} />{grp.name}</span>
                        : c.group_name
                        ? <span className="text-xs text-slate-400">{c.group_name}</span>
                        : <span className="text-xs text-slate-300">—</span>}
                    </td>
                    <td className="px-5 py-3">
                      <button onClick={() => deleteContact(c.id)} className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 p-4 border-t border-slate-50">
            <button onClick={() => setPage(p => Math.max(1, p-1))} disabled={page === 1} className="p-1.5 rounded-lg border border-slate-200 disabled:opacity-40"><ChevronLeft className="w-4 h-4" /></button>
            <span className="text-sm text-slate-600">Page {page}/{totalPages}</span>
            <button onClick={() => setPage(p => Math.min(totalPages, p+1))} disabled={page === totalPages} className="p-1.5 rounded-lg border border-slate-200 disabled:opacity-40"><ChevronRight className="w-4 h-4" /></button>
          </div>
        )}
      </div>
    </div>
  );
}
